import { makeAutoObservable } from 'mobx';
import {
    BridgeHandlerDef,
    BridgeResponse,
    WebViewAppSendEventName,
    WebViewPublishEventName,
} from '../../types/bridgeTypes';
import { PushMessage } from '../../types/pushTypes';
import { apiClient } from '../utils/apiClient';
import firebaseStore from './firebaseStore';
import notificationBarStore from './notificationBarStore';
import webViewSyncGlobalHandlers from './webViewSyncGlobalHandlers';
import {
    WebViewSyncHandler,
    WebViewSyncKey,
    WebViewSyncParams,
} from '../../types/webViewSyncTypes';
import _ from 'lodash';

/**
 * 웹뷰 브릿지 스토어
 */
class WebViewStore {
    isInitialized = false;
    isWebView = false;
    bridgeVersion = 0;
    deviceInfo: Window['HaewaoAppBridge']['deviceInfo'] | null = null;
    navigationInfo: Window['HaewaoAppBridge']['navigationInfo'] | null = null;
    syncUiHandlers: Map<WebViewSyncKey, Set<WebViewSyncHandler<WebViewSyncKey>>> = new Map();

    /**
     * 탭을 더블 클릭했을 때 새로고침하기 위한 변수
     */
    tabRepressed: {
        tabName: string;
        timestamp: number;
    } | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    initialize() {
        // 웹뷰가 로드되기 전에 객체가 존재하기 때문에 항상 있지만,
        // 혹시 모를 예외를 위해 HaewaoAppBridgeReady 이벤트까지 처리함
        if (window.HaewaoAppBridge) {
            this.isWebView = true;
            this.bridgeVersion = window.HaewaoAppBridge.version;
            this.deviceInfo = window.HaewaoAppBridge.deviceInfo;
            this.navigationInfo = window.HaewaoAppBridge.navigationInfo;

            // 앱에서 현재 웹뷰에 보내는 이벤트
            window.addEventListener('HaewaoAppBridgeMessageFromApp', (event: Event) => {
                const customEvent = event as CustomEvent<{
                    eventName: WebViewAppSendEventName;
                    eventData: unknown;
                }>;
                this.onMessageFromApp(customEvent.detail);
            });

            // 다른 웹뷰에서 온 이벤트
            // - webViewStore.publishToOtherWebViews() 로 전파한 이벤트
            window.addEventListener('HaewaoAppBridgeMessageFromWebView', (event: Event) => {
                const customEvent = event as CustomEvent<{
                    eventName: WebViewPublishEventName;
                    eventData: unknown;
                }>;
                this.onMessageFromWebView(customEvent.detail);
            });
        } else {
            window.addEventListener('HaewaoAppBridgeReady', () => {
                this.isWebView = true;
                this.bridgeVersion = window.HaewaoAppBridge.version;
            });
        }

        this.isInitialized = true;
    }

    async sendMessageToApp<T extends keyof BridgeHandlerDef>(
        type: T,
        payload?: BridgeHandlerDef[T]['payload'],
    ): Promise<BridgeResponse<T>> {
        if (!this.isWebView) {
            return {
                requestId: '',
                payload,
                success: false,
                error: 'Bridge is not ready',
            };
        }

        const isIgnoreTimeout = type === 'loginWithGoogle' || type === 'loginWithApple';

        const response = await window.HaewaoAppBridge.sendMessageToApp(type, payload, {
            ignoreTimeout: isIgnoreTimeout,
        });

        return response;
    }

    async publishToOtherWebViews(
        eventName: WebViewPublishEventName,
        eventData?: unknown,
    ): Promise<BridgeResponse<'publishToOtherWebViews'>> {
        return this.sendMessageToApp('publishToOtherWebViews', { eventName, eventData });
    }

    async onMessageFromWebView({
        eventName,
        eventData,
    }: {
        eventName: WebViewPublishEventName;
        eventData: unknown;
    }) {
        switch (eventName) {
            case 'bridge-test':
                alert('bridge-test');
                break;
            case 'login-succeed': {
                // 상호참조 이슈로 비동기로 import 함
                await firebaseStore.updateMe();
                break;
            }
            case 'logout-succeed': {
                await firebaseStore.updateMe();
                break;
            }
            case 'sync-ui': {
                const { syncKey, data } = eventData as {
                    syncKey: WebViewSyncKey;
                    data: unknown;
                };

                const globalHandler = webViewSyncGlobalHandlers[syncKey];
                if (globalHandler) {
                    await globalHandler(data as any);
                }

                const handlers = this.syncUiHandlers.get(syncKey);
                if (handlers) {
                    for (const handler of handlers) {
                        await handler(data as any);
                    }
                }
                break;
            }
            default:
                break;
        }
    }

    /**
     * 다른 웹뷰에 메시지를 보내는 것이지만 타입 안전성을 위해 메서드로 따로 빼놓음
     */
    async syncUi<T extends WebViewSyncKey>(syncKey: T, data: WebViewSyncParams[T]) {
        await this.publishToOtherWebViews('sync-ui', {
            syncKey,
            data,
        });
    }

    async addSyncUiHandler<T extends WebViewSyncKey>(syncKey: T, handler: WebViewSyncHandler<T>) {
        const handlers = this.syncUiHandlers.get(syncKey) || new Set();
        handlers.add(handler as any);
        this.syncUiHandlers.set(syncKey, handlers);
    }

    async removeSyncUiHandler<T extends WebViewSyncKey>(
        syncKey: T,
        handler: WebViewSyncHandler<T>,
    ) {
        const handlers = this.syncUiHandlers.get(syncKey);
        if (handlers) {
            handlers.delete(handler as any);
            this.syncUiHandlers.set(syncKey, handlers);
        }
    }

    async onMessageFromApp({
        eventName,
        eventData,
    }: {
        eventName: WebViewAppSendEventName;
        eventData: unknown;
    }) {
        switch (eventName) {
            // 앱이 실행 중에 푸시를 받은 경우
            case 'push-received': {
                const { message } = eventData as {
                    message: PushMessage;
                };

                notificationBarStore.show({
                    title: message.notification.title,
                    text: message.notification.body,
                    onClick: async () => {
                        const isLoggedIn = await firebaseStore.isLoggedIn();

                        const campaignKey = message.data?.campaignKey;
                        if (campaignKey && isLoggedIn) {
                            await apiClient.notification.member.markAsPushClicked.mutate({
                                campaignKey,
                            });
                        }

                        const link = message.data?.link;
                        if (link) {
                            this.sendMessageToApp('openWebView', { url: link });
                        }
                    },
                });
                break;
            }
            // 푸시 알림을 선택해서 백그라운드에서 앱이 다시 실행된 경우
            // - 웹뷰에서 해당 푸시를 읽음 처리해줘야 함
            case 'push-read': {
                const { message } = eventData as {
                    message: PushMessage;
                };

                const isLoggedIn = await firebaseStore.isLoggedIn();

                const campaignKey = message.data?.campaignKey;
                if (campaignKey && isLoggedIn) {
                    await apiClient.notification.member.markAsPushClicked.mutate({
                        campaignKey,
                    });
                }
                break;
            }
            case 'tab-repressed': {
                const { tabName } = eventData as {
                    tabName: string;
                };

                try {
                    // 탭이 중복 클릭됐다면 먼저 스크롤을 위로 올리고, 맨 위라면 새로고침 해준다
                    if (window.scrollY === 0) {
                        if (this.tabRepressed?.tabName === tabName) {
                            const timeDiff = Date.now() - this.tabRepressed.timestamp;
                            // 더블 클릭했을 때에만 새로고침
                            if (timeDiff < 300) {
                                location.reload();
                            }
                        }

                        this.tabRepressed = {
                            tabName,
                            timestamp: Date.now(),
                        };
                    } else {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                } catch (error) {
                    console.log(error);
                }

                break;
            }
            default:
                break;
        }
    }
}

export default new WebViewStore();
